import {
  HeatmapSurchargeBulkApprovalResponse,
  HeatmapSurchargeCalendar,
  HeatmapSurchargeCalendarFilters,
  HeatmapSurchargeOffer,
  HeatmapSurchargeOfferList,
  HeatmapSurchargePricingStatus,
  HeatmapSurchargeQuoteStatus,
  HeatmapSurchargeRecordedApproval,
} from '~/types/heatmapSurcharge';

import { json_headers, request } from './common';

type RequestOptions = { signal?: AbortSignal };

type OfferResponse = {
  offer_id: string;
  offer_name: string | null;
  vendor_name: string | null;
  generated_at: string;
};

type CalendarResponse = OfferResponse & {
  room_types: Array<{ room_type_id: string; room_type_name: string | null }>;
  selected_room_type_id: string;
  check_in_months: Array<string>;
  selected_check_in_month: string;
  room_nights: Array<{
    travel_date: string;
    booked_at_generation: number | null;
    allocated_at_generation: number | null;
    predicted_additional_bookings: number;
    predicted_final_bookings: number | null;
    predicted_rooms_left: number | null;
    forecast_exceeds_allocation_by: number | null;
    source_snapshot_at: string | null;
  }>;
  recommendations: Array<{
    travel_date: string;
    predicted_final_bookings: number;
    predicted_rooms_left: number;
    predicted_sold_percentage: number;
    suggested_surcharge_percentage: number;
  }>;
  pricing_context: {
    status: HeatmapSurchargePricingStatus;
    prepared_at: string | null;
    source_snapshot_date: string | null;
  };
  pricing_references: Array<{
    travel_date: string;
    room_rate_id: string;
    base_nightly_price: number;
    vendor_surcharge_total: number;
    internal_surcharge_total: number;
    current_nightly_price: number;
  }>;
  surcharge_quotes: Array<{
    travel_date: string;
    room_rate_id: string;
    suggested_surcharge_percentage: number;
    base_nightly_price: number;
    vendor_surcharge_total: number;
    current_internal_surcharge: number;
    current_nightly_price: number;
    target_internal_surcharge: number;
    additional_internal_surcharge: number;
    proposed_nightly_price: number;
    status: HeatmapSurchargeQuoteStatus;
    approval: {
      job_id: string | null;
      status: HeatmapSurchargeRecordedApproval['status'];
      target_internal_surcharge: number;
    } | null;
  }>;
};

type BulkApprovalResponse = {
  results: Array<{
    travel_date: string;
    status: 'submitted' | 'skipped' | 'failed';
    reason: HeatmapSurchargeBulkApprovalResponse['results'][number]['reason'];
    message: string | null;
    job_id: string | null;
    approval: {
      job_id: string | null;
      status: HeatmapSurchargeRecordedApproval['status'];
      target_internal_surcharge: number;
    } | null;
  }>;
  submitted_count: number;
  skipped_count: number;
  failed_count: number;
};

function basePath() {
  return `${
    window.configs.LERE_API_HOST ?? window.configs.API_HOST
  }/api/recommendation/admin/hotel-sellout/heatmap-surcharge`;
}

function mapOffer(response: OfferResponse): HeatmapSurchargeOffer {
  return {
    offerId: response.offer_id,
    offerName: response.offer_name,
    vendorName: response.vendor_name,
    generatedAt: response.generated_at,
  };
}

async function getOffers(offerId?: string, options?: RequestOptions): Promise<HeatmapSurchargeOfferList> {
  const params = new URLSearchParams();
  if (offerId) {
    params.set('offer_id', offerId);
  }
  const queryString = params.toString();
  const response: { offers: Array<OfferResponse>; count: number } = await request(
    `${basePath()}${queryString ? `?${queryString}` : ''}`,
    { method: 'GET', headers: json_headers, signal: options?.signal },
  );

  return { offers: response.offers.map(mapOffer), count: response.count };
}

async function getCalendar(
  offerId: string,
  filters: HeatmapSurchargeCalendarFilters = {},
  options?: RequestOptions,
): Promise<HeatmapSurchargeCalendar> {
  const params = new URLSearchParams();
  if (filters.roomTypeId) {
    params.set('room_type_id', filters.roomTypeId);
  }
  if (filters.checkInMonth) {
    params.set('check_in_month', filters.checkInMonth);
  }
  const queryString = params.toString();
  const response: CalendarResponse = await request(
    `${basePath()}/${encodeURIComponent(offerId)}${queryString ? `?${queryString}` : ''}`,
    { method: 'GET', headers: json_headers, signal: options?.signal },
  );

  return {
    ...mapOffer(response),
    roomTypes: response.room_types.map((room) => ({
      roomTypeId: room.room_type_id,
      roomTypeName: room.room_type_name,
    })),
    selectedRoomTypeId: response.selected_room_type_id,
    checkInMonths: response.check_in_months,
    selectedCheckInMonth: response.selected_check_in_month,
    roomNights: response.room_nights.map((night) => ({
      travelDate: night.travel_date,
      bookedAtGeneration: night.booked_at_generation,
      allocatedAtGeneration: night.allocated_at_generation,
      predictedAdditionalBookings: night.predicted_additional_bookings,
      predictedFinalBookings: night.predicted_final_bookings,
      predictedRoomsLeft: night.predicted_rooms_left,
      forecastExceedsAllocationBy: night.forecast_exceeds_allocation_by,
      sourceSnapshotAt: night.source_snapshot_at,
    })),
    recommendations: response.recommendations.map((recommendation) => ({
      travelDate: recommendation.travel_date,
      predictedFinalBookings: recommendation.predicted_final_bookings,
      predictedRoomsLeft: recommendation.predicted_rooms_left,
      predictedSoldPercentage: recommendation.predicted_sold_percentage,
      suggestedSurchargePercentage: recommendation.suggested_surcharge_percentage,
    })),
    pricingContext: {
      status: response.pricing_context.status,
      preparedAt: response.pricing_context.prepared_at,
      sourceSnapshotDate: response.pricing_context.source_snapshot_date,
    },
    pricingReferences: response.pricing_references.map((reference) => ({
      travelDate: reference.travel_date,
      roomRateId: reference.room_rate_id,
      baseNightlyPrice: reference.base_nightly_price,
      vendorSurchargeTotal: reference.vendor_surcharge_total,
      internalSurchargeTotal: reference.internal_surcharge_total,
      currentNightlyPrice: reference.current_nightly_price,
    })),
    surchargeQuotes: response.surcharge_quotes.map((quote) => ({
      travelDate: quote.travel_date,
      roomRateId: quote.room_rate_id,
      suggestedSurchargePercentage: quote.suggested_surcharge_percentage,
      baseNightlyPrice: quote.base_nightly_price,
      vendorSurchargeTotal: quote.vendor_surcharge_total,
      currentInternalSurcharge: quote.current_internal_surcharge,
      currentNightlyPrice: quote.current_nightly_price,
      targetInternalSurcharge: quote.target_internal_surcharge,
      additionalInternalSurcharge: quote.additional_internal_surcharge,
      proposedNightlyPrice: quote.proposed_nightly_price,
      status: quote.status,
      approval: quote.approval
        ? {
            jobId: quote.approval.job_id,
            status: quote.approval.status,
            targetInternalSurcharge: quote.approval.target_internal_surcharge,
          }
        : null,
    })),
  };
}

async function approveBulk(
  offerId: string,
  roomTypeId: string,
  travelDates: Array<string>,
  note?: string,
): Promise<HeatmapSurchargeBulkApprovalResponse> {
  const response: BulkApprovalResponse = await request(`${basePath()}/${encodeURIComponent(offerId)}/apply`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ room_type_id: roomTypeId, travel_dates: travelDates, note: note || undefined }),
  });
  return {
    results: response.results.map((result) => ({
      travelDate: result.travel_date,
      status: result.status,
      reason: result.reason,
      message: result.message,
      jobId: result.job_id,
      approval: result.approval
        ? {
            jobId: result.approval.job_id,
            status: result.approval.status,
            targetInternalSurcharge: result.approval.target_internal_surcharge,
          }
        : null,
    })),
    submittedCount: response.submitted_count,
    skippedCount: response.skipped_count,
    failedCount: response.failed_count,
  };
}

async function approve(
  offerId: string,
  roomTypeId: string,
  travelDate: string,
  note?: string,
): Promise<{ jobId: string }> {
  const response = await approveBulk(offerId, roomTypeId, [travelDate], note);
  const result = response.results[0];
  if (result.status !== 'submitted' || !result.jobId) {
    throw new Error(result.message || 'Heatmap surcharge was not submitted.');
  }
  return { jobId: result.jobId };
}

export default { getOffers, getCalendar, approve, approveBulk };
